/**
 * Get a quote — stepped enquiry.
 *
 * Make → Model → Year → details, driven by the same `model` metaobject data the
 * vehicle picker uses.
 *
 * The design constraint that shapes everything here: a customer must always be
 * able to finish. The competitor's version is a locked funnel — an unlisted
 * make, model or year ends the journey. So every step can be skipped, the
 * vehicle field on the form is free text rather than a hidden value, and
 * skipping carries forward whatever was chosen ("Ford — model not listed")
 * instead of throwing it away.
 */

const STEPS = ['make', 'model', 'year', 'form'];

class QuoteBuilder {
  constructor(root) {
    this.root = root;
    this.picked = { make: '', model: '', year: '' };

    let data = { models: [] };
    try {
      data = JSON.parse(root.querySelector('[data-qb-data]').textContent);
    } catch (e) {
      console.warn('Quote builder: could not parse vehicle data', e);
    }
    this.models = Array.isArray(data.models) ? data.models : [];

    // Brand → logo, emitted by Liquid. The app proxy carries no imagery, so the
    // logos have to come from the `brand` metaobjects alongside it.
    this.logos = {};
    const logoEl = root.querySelector('[data-qb-logos]');
    if (logoEl) {
      try {
        this.logos = JSON.parse(logoEl.textContent) || {};
      } catch (e) {
        console.warn('Quote builder: could not parse brand logos', e);
      }
    }

    // The inline payload is Liquid's first page of metaobjects — 50 rows, which
    // silently drops most makes. Pull the complete set from the app proxy the
    // dashboard backs, the same source the vehicle drawer uses.
    this.loadFullList(root.dataset.qbVehiclesUrl);

    this.steps = new Map(STEPS.map((n) => [n, root.querySelector(`[data-qb-step="${n}"]`)]));
    this.dots = new Map(STEPS.map((n) => [n, root.querySelector(`[data-qb-dot="${n}"]`)]));
    this.vehicleInput = root.querySelector('[data-qb-vehicle]');

    this.bind();
    this.renderMakes();

    // A submitted form comes back with ?form_type=... and the success message
    // rendered inside the last step — so open there rather than at step one.
    if (root.querySelector('.qb__ok')) this.show('form');
    else this.show('make');
  }

  async loadFullList(url) {
    if (!url) return;
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const data = await res.json();
      if (!data || !Array.isArray(data.models) || !data.models.length) return;

      const now = new Date().getFullYear();
      this.models = data.models.map((m) => {
        let endYear = Number(m.endYear) || 0;
        // A model still in production carries an end year in the past; treat it
        // as current so this year is offered. Mirrors vehicle-drawer.js.
        if (!m.discontinued && endYear && endYear < now) endYear = now;
        return { brand: m.brand, model: m.model, startYear: Number(m.startYear) || 0, endYear };
      });

      // Only redraw the make list if the customer hasn't started choosing —
      // swapping options underneath them mid-flow would be worse than a short list.
      if (!this.picked.make) this.renderMakes();
    } catch {
      /* Seed data stays in place — a short list beats a broken one. */
    }
  }

  bind() {
    this.root.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const option = target.closest('[data-qb-value]');
      if (option) return this.choose(option.dataset.qbFor, option.dataset.qbValue);

      const skip = target.closest('[data-qb-skip]');
      if (skip) return this.skip(skip.dataset.qbSkip);

      const back = target.closest('[data-qb-back]');
      if (back) return this.back(back.dataset.qbBack);
    });
  }

  /** Unique, sorted values — the metaobject set has one row per model. */
  makes() {
    return [...new Set(this.models.map((m) => m.brand).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b)
    );
  }

  modelsFor(make) {
    return [...new Set(this.models.filter((m) => m.brand === make).map((m) => m.model))].sort((a, b) =>
      a.localeCompare(b)
    );
  }

  yearsFor(make, model) {
    const row = this.models.find((m) => m.brand === make && m.model === model);
    if (!row) return [];
    const from = Number(row.startYear) || 0;
    const to = Number(row.endYear) || 0;
    if (!from || !to || to < from) return [];
    const years = [];
    for (let y = to; y >= from; y--) years.push(String(y));
    return years;
  }

  renderOptions(name, values) {
    const host = this.root.querySelector(`[data-qb-options="${name}"]`);
    if (!host) return;
    if (!values.length) {
      host.innerHTML = `<p class="qb__empty">Nothing listed here — use the link below and we'll sort it out.</p>`;
      return;
    }
    host.innerHTML = values
      .map((v) => {
        // Logos only exist for makes, and only for brands that have one — the
        // rest fall back to the name on its own rather than a gap.
        const logo = name === 'make' ? this.logos[v] : null;
        const mark = logo
          ? `<img class="qb__option-logo" src="${escapeAttr(logo)}" alt="" loading="lazy" width="28" height="28">`
          : '';
        return `<button type="button" class="qb__option${
          mark ? ' qb__option--logo' : ''
        }" data-qb-for="${name}" data-qb-value="${escapeAttr(v)}">${mark}<span>${escapeHtml(
          v
        )}</span></button>`;
      })
      .join('');
  }

  renderMakes() {
    this.renderOptions('make', this.makes());
  }

  choose(name, value) {
    this.picked[name] = value;
    if (name === 'make') {
      this.picked.model = '';
      this.picked.year = '';
      this.renderOptions('model', this.modelsFor(value));
      return this.show('model');
    }
    if (name === 'model') {
      this.picked.year = '';
      this.renderOptions('year', this.yearsFor(this.picked.make, value));
      return this.show('year');
    }
    return this.show('form');
  }

  /**
   * Skipping is not "give up" — it carries the picks made so far into the form
   * and names the gap, so the enquiry still tells us something useful.
   */
  skip(from) {
    if (from === 'make') this.picked = { make: '', model: '', year: '' };
    if (from === 'model') this.picked.model = '';
    if (from === 'year') this.picked.year = '';
    this.show('form');
  }

  back(to) {
    if (to === 'make') this.renderMakes();
    if (to === 'model') this.renderOptions('model', this.modelsFor(this.picked.make));
    this.show(to);
  }

  /** What goes in the vehicle box — always editable once it lands there. */
  vehicleSummary() {
    const { make, model, year } = this.picked;
    if (!make) return '';
    if (!model) return `${make} — model not listed`;
    if (!year) return `${make} ${model} — year not listed`;
    return `${make} ${model} ${year}`;
  }

  show(name) {
    for (const [key, el] of this.steps) {
      if (el) el.hidden = key !== name;
    }

    const index = STEPS.indexOf(name);
    for (const [key, dot] of this.dots) {
      if (!dot) continue;
      const at = STEPS.indexOf(key);
      dot.toggleAttribute('data-current', at === index);
      dot.toggleAttribute('data-done', at < index);
    }

    if (name === 'form' && this.vehicleInput && !this.vehicleInput.value) {
      this.vehicleInput.value = this.vehicleSummary();
    }

    // Don't yank the page on first paint — only when the customer advances.
    if (this.ready) {
      const top = this.root.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    this.ready = true;
  }
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

function escapeAttr(value) {
  return escapeHtml(value);
}

for (const root of document.querySelectorAll('[data-quote-builder]')) {
  new QuoteBuilder(root);
}
